import React, { memo, useMemo } from 'react';
import { MdInventory2, MdLayers } from 'react-icons/md';
import CollectionMediaViewer from './CollectionMediaViewer';
import type { Collection } from '../../types/collections.types';

interface CollectionDetailPanelProps {
  collection: Collection | null;
}

/** Parse products_list robustly */
function getProducts(col: Collection): any[] {
  if (col.transformed_products?.length) return col.transformed_products;
  if (!col.products_list) return [];

  let list = col.products_list;
  if (typeof list === 'string') {
    try { list = JSON.parse(list as any); } catch { return []; }
  }
  if (Array.isArray(list) && list.length === 1 && typeof list[0] === 'string') {
    try { list = JSON.parse(list[0]); } catch { /* */ }
  }
  return Array.isArray(list) ? list : [];
}


/** Masonry height classes – varied aspect ratios for visual interest */
const MASONRY_HEIGHTS = [
  'aspect-[3/4]',   // tall portrait
  'aspect-[4/5]',   // slightly tall
  'aspect-square',  // square
  'aspect-[3/4]',   // tall portrait
  'aspect-[2/3]',   // tallest
  'aspect-[4/5]',   // slightly tall
  'aspect-square',  // square
  'aspect-[3/4]',   // tall portrait
];

const ProductCard: React.FC<{ product: any; index: number }> = memo(({ product, index }) => {
  const heightClass = MASONRY_HEIGHTS[index % MASONRY_HEIGHTS.length];

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden bg-[#1a1a1a] border border-white/[0.05] hover:border-white/[0.18] transition-all duration-500 hover:shadow-[0_8px_40px_rgba(0,0,0,0.5)] break-inside-avoid mb-4 ${heightClass}`}
    >
      {/* Full-bleed image */}
      <img
        src={product.image || 'https://via.placeholder.com/400'}
        alt={product.lowercase_title || product.title || 'Product'}
        className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.08] transition-transform duration-700 ease-out"
        loading="lazy"
      />

      {/* Subtle permanent bottom vignette for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

      {/* Product info on hover */}
      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400 ease-out">
        <h4 className="text-[13px] font-semibold text-white capitalize line-clamp-2 leading-snug drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          {product.lowercase_title || product.title || product.sku}
        </h4>
        {product.price && (
          <p className="text-[12px] text-white/60 mt-1 font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{product.price}</p>
        )}
      </div>

      {/* Corner accent on hover */}
      <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white/0 group-hover:bg-white/40 transition-all duration-500 group-hover:shadow-[0_0_8px_rgba(255,255,255,0.3)]" />
    </div>
  );
});
ProductCard.displayName = 'ProductCard';

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------
const CollectionDetailPanel: React.FC<CollectionDetailPanelProps> = ({ collection }) => {
  const products = useMemo(
    () => (collection ? getProducts(collection) : []),
    [collection]
  );

  // Empty state
  if (!collection) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/10 to-purple-500/10 flex items-center justify-center mx-auto mb-5">
            <MdLayers size={36} className="text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-300 mb-2">
            Select a collection
          </h3>
          <p className="text-[13px] text-gray-500 leading-relaxed">
            Choose a collection from the sidebar to view its media, description, and products.
          </p>
        </div>
      </div>
    );
  }

  const isActive = collection.status === 'active';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* ── Scrollable Content ── */}
      <div
        className="flex-1 overflow-y-auto bg-[#0f0f0f]"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.06) transparent' }}
      >
        <div className="max-w-5xl mx-auto px-6 py-5 space-y-5">
          {/* ── Media Viewer (Image/Video tabs) ── */}
          <CollectionMediaViewer
            collectionId={collection.pk_id}
            collection={collection}
          />

          {/* ── Collection Info ── */}
          <div className="space-y-3">
            {/* Title + status */}
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-white line-clamp-1">
                {collection.title}
              </h1>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${
                isActive
                  ? 'bg-green-500/15 text-green-400 border border-green-500/20'
                  : 'bg-gray-600/15 text-gray-400 border border-gray-500/20'
              }`}>
                {isActive ? 'Live' : 'Draft'}
              </span>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 flex-shrink-0">
                <MdInventory2 size={12} className="text-purple-400" />
                <span className="text-[11px] font-semibold text-white">{products.length}</span>
                <span className="text-[10px] text-gray-400">Products</span>
              </div>
            </div>

            {/* Description */}
            {collection.short_desc && (
              <p
                className="text-[13px] text-gray-400 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: collection.short_desc }}
              />
            )}
          </div>

          {/* ── Products Grid ── */}
          {products.length > 0 ? (
            <div>
              <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-3">
                Products
              </h3>
              <div className="columns-2 lg:columns-3 gap-4">
                {products.map((product: any, idx: number) => (
                  <ProductCard key={product.sku || idx} product={product} index={idx} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <MdInventory2 size={36} className="text-gray-600 mx-auto mb-2" />
                <p className="text-[13px] text-gray-400">No products in this collection</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CollectionDetailPanel.displayName = 'CollectionDetailPanel';
export default CollectionDetailPanel;
